type MediaSourceConstructor = typeof MediaSource;
type StreamingListener = (streaming: boolean) => void;

interface IMediaSourceWindow {
  MediaSource?: MediaSourceConstructor;
  ManagedMediaSource?: MediaSourceConstructor;
}

export interface IMediaSourceSession {
  readonly mediaSource: MediaSource;
  attach: (element: HTMLVideoElement) => void;
  detach: () => void;
  onStreamingChange: (listener: StreamingListener) => void;
}

export const getMediaSourceConstructor = (): MediaSourceConstructor | undefined => {
  const target = window as unknown as IMediaSourceWindow;

  return target.MediaSource ?? target.ManagedMediaSource;
};

const removeSourceChildren = (element: HTMLVideoElement) => {
  element.querySelectorAll("source").forEach((source) => source.remove());
};

const resetElement = (element: HTMLVideoElement) => {
  element.src = "";
  element.load();
};

const createPlainSession = (mediaSource: MediaSource): IMediaSourceSession => {
  let element: HTMLVideoElement | undefined;

  return {
    mediaSource,
    attach: (target) => {
      element = target;
      target.src = URL.createObjectURL(mediaSource);
    },
    detach: () => {
      if (element) {
        resetElement(element);
      }

      element = undefined;
    },
    onStreamingChange: () => undefined,
  };
};

const createManagedSession = (mediaSource: MediaSource): IMediaSourceSession => {
  let element: HTMLVideoElement | undefined;
  let listener: StreamingListener | undefined;

  const startStreaming = () => {
    listener?.(true);
  };

  const endStreaming = () => {
    if (element?.seeking) {
      return;
    }

    listener?.(false);
  };

  return {
    mediaSource,
    attach: (target) => {
      element = target;

      mediaSource.addEventListener("startstreaming", startStreaming);
      mediaSource.addEventListener("endstreaming", endStreaming);
      target.addEventListener("seeking", startStreaming);

      target.removeAttribute("src");
      target.disableRemotePlayback = true;
      removeSourceChildren(target);

      const source = document.createElement("source");
      source.type = "video/mp4";
      source.src = URL.createObjectURL(mediaSource);
      target.appendChild(source);
      target.load();
    },
    detach: () => {
      mediaSource.removeEventListener("startstreaming", startStreaming);
      mediaSource.removeEventListener("endstreaming", endStreaming);

      if (element) {
        element.removeEventListener("seeking", startStreaming);
        removeSourceChildren(element);
        resetElement(element);
      }

      element = undefined;
      listener = undefined;
    },
    onStreamingChange: (next) => {
      listener = next;
    },
  };
};

export const createMediaSourceSession = (): IMediaSourceSession | undefined => {
  const target = window as unknown as IMediaSourceWindow;

  if (target.MediaSource) {
    return createPlainSession(new target.MediaSource());
  }

  if (target.ManagedMediaSource) {
    return createManagedSession(new target.ManagedMediaSource());
  }

  return undefined;
};
